import { FunctionComponent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import DatePicker from '@/components/_custom/Picker/datePicker/datePicker'
import moment from 'moment/moment'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker'
import TextArea from '@/components/_custom/TextArea/textArea'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './requestSlotDialog.module.scss'
import { Daycare } from '@/types/Daycare'
import { useMutation } from '@apollo/client'
import { CREATE_SLOT_REQUEST } from '@/modules/API/slot/requestSlotsHandler'
import { getDaycareClosing, getDaycareOpening, getNextOpeningDate } from '@/modules/helper/daycareHelper'
import { CreateChildRequestSlot } from '@/types/Slots'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface RequestSlotDialogProps {
    open: boolean
    handleClose: () => void
    refetchData: () => void
    childs?: CreateChildRequestSlot[]
    currentDaycare: Daycare
}

const RequestSlotDialog: FunctionComponent<RequestSlotDialogProps> = ({
    open,
    handleClose,
    childs,
    refetchData,
    currentDaycare
}: RequestSlotDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const selectOptions: SelectOptionsDeprecated = [
        {
            options:
                childs?.map((child: CreateChildRequestSlot): SelectOptionDeprecated => {
                    return {
                        value: child.childId.toString(),
                        name: child.childName + (child.childComplement ? ` - ${child.childComplement}` : '')
                    }
                }) || []
        }
    ]

    const [selectChilds, setSelectChilds] = useState<string[]>(childs?.map((child) => child.childId.toString()) || [])

    const [date, setDate] = useState<string>(
        getNextOpeningDate(currentDaycare, moment().add(1, 'd')).format('DD-MM-YYYY')
    )

    const [start, setStart] = useState<string>(
        getDaycareOpening(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7) || '08:00'
    )
    const [end, setEnd] = useState<string>(
        getDaycareClosing(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7) || '18:00'
    )

    const [comment, setComment] = useState<string>('')

    useEffect(() => {
        setDate(getNextOpeningDate(currentDaycare, moment(date, 'DD-MM-YYYY')).format('DD-MM-YYYY'))
        setStart(getDaycareOpening(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7) || '08:00')
        setEnd(getDaycareClosing(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7) || '18:00')
    }, [date])

    const [createRequestSlot, { loading: loadingCreate }] = useMutation(CREATE_SLOT_REQUEST, {
        onCompleted: () => {
            handleClose()
            refetchData()

            showNotificationSuccess(t('requestSlotSuccess'))
        },
        onError: () => {
            showNotificationError(t('requestSlotError'))
        }
    })

    function createAllRequestSlots(): void {
        selectChilds.forEach((childId: string) => {
            createRequestSlot({
                variables: {
                    daycare_id: currentDaycare.id,
                    child_id: parseInt(childId),
                    date: date,
                    start: start,
                    end: end,
                    comment: comment
                }
            })
        })
    }

    const minHour: number = Number(
        moment(getDaycareOpening(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7), 'HH:mm').format('HH')
    )
    const minMinute: number = Number(
        moment(getDaycareOpening(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7), 'HH:mm').format('mm')
    )
    const maxHour: number = Number(
        moment(getDaycareClosing(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7), 'HH:mm').format('HH')
    )
    const maxMinute: number = Number(
        moment(getDaycareClosing(currentDaycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7), 'HH:mm').format('mm')
    )

    const range: number = currentDaycare.merger ? Number(currentDaycare.bookRange.split('_')[1] || '15') : 15

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('requestSlot')} parent />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <SelectMultiple
                            label={t('myChilds')}
                            selectOptions={selectOptions}
                            setSelectValues={setSelectChilds}
                            selectValues={selectChilds}
                            isParent
                            className={styles.UserInput}
                        />
                    </div>
                    <div className={styles.Input}>
                        <DatePicker
                            label={t('date')}
                            value={date}
                            setValue={setDate}
                            parent
                            className={styles.UserInput}
                            disablePast
                            minDate={moment().add(1, 'd').format('DD-MM-YYYY')}
                        />
                    </div>
                    <div className={styles.Input}>
                        <TimePicker
                            label={t('startTime')}
                            value={start}
                            setValue={setStart}
                            parent
                            className={styles.UserInput}
                            minHour={minHour}
                            minMinute={minMinute}
                            maxHour={maxHour}
                            maxMinute={maxMinute}
                            endTime={
                                end
                                    ? moment(end, 'HH:mm').format('HH:mm')
                                    : moment().hours(maxHour).minutes(maxMinute).seconds(0).format('HH:mm')
                            }
                            minuteToDisable={range}
                        />
                        <TimePicker
                            value={end}
                            setValue={setEnd}
                            label={t('endTime')}
                            parent
                            className={styles.UserInput}
                            minHour={minHour}
                            minMinute={minMinute}
                            maxHour={maxHour}
                            maxMinute={maxMinute}
                            startTime={
                                start
                                    ? moment(start, 'HH:mm').format('HH:mm')
                                    : moment().hours(minHour).minutes(minMinute).seconds(0).format('HH:mm')
                            }
                            minuteToDisable={range}
                        />
                    </div>
                    <div className={styles.TextAreaInput}>
                        <p className={styles.TextAreaTitle}>{t('comment')}</p>
                        <TextArea
                            placeholder={t('comment')}
                            value={comment}
                            onChange={(e) => setComment(e)}
                            maxCount={100}
                            parent
                            className={styles.TextArea}
                        />
                    </div>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} parent onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'confirm'}
                        parent
                        className={styles.Button}
                        onClick={createAllRequestSlots}
                        disabled={selectChilds.length === 0 || loadingCreate}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default RequestSlotDialog
